import { useEffect, useId, useRef, useState } from 'react'
import { ChevronDown, X } from 'lucide-react'
import type { SelectOption } from '../../types'
import { filterSelectOptions } from './helpers/filterSelectOptions'
import type { SearchableSelectProps } from './SearchableSelect.types'
import './SearchableSelect.css'
import { sortSelectOptions } from '../../helpers/sortSelectOptions'

export function SearchableSelect({
  value,
  options: unsortedOptions,
  onChange,
  onBlur,
  id,
  ariaLabel,
  placeholder = 'Select…',
  searchMode = 'contains',
  isDisabled = false,
  isRequired = false,
  isInvalid = false,
  className,
}: SearchableSelectProps) {
  const options = sortSelectOptions(unsortedOptions)
  const generatedId = useId()
  const inputId = id ?? `${generatedId}-input`
  const listboxId = `${generatedId}-listbox`
  const listboxRef = useRef<HTMLUListElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)

  const visibleOptions =
    query === null ? options : filterSelectOptions(options, query)
  const selectedLabel =
    options.find((option) => option.value === value)?.label ?? ''
  const hasValue = value !== ''
  const canClear = hasValue && !isDisabled
  const isSearchable = searchMode === 'contains'
  const activeOption = isOpen ? visibleOptions[activeIndex] : undefined
  const buildOptionId = (index: number) => `${listboxId}-option-${index}`

  useEffect(() => {
    if (!isOpen) {
      return
    }
    listboxRef.current
      ?.querySelector(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView?.({ block: 'nearest' })
  }, [isOpen, activeIndex])

  function openList() {
    setIsOpen(true)
    setActiveIndex(
      Math.max(
        0,
        options.findIndex((option) => option.value === value)
      )
    )
  }

  function closeList() {
    setIsOpen(false)
    setQuery(null)
  }

  function chooseOption(option: SelectOption) {
    onChange(option.value)
    closeList()
  }

  function clearValue() {
    onChange('')
    closeList()
    inputRef.current?.focus()
  }

  function handleInputChange(text: string) {
    setQuery(text)
    setIsOpen(true)
    setActiveIndex(0)
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    const isOpenKey = event.key === 'Enter' || event.key === ' '
    if (!isOpen && !isSearchable && isOpenKey) {
      event.preventDefault()
      openList()
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (!isOpen) {
        openList()
        return
      }
      setActiveIndex((index) => Math.min(index + 1, visibleOptions.length - 1))
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((index) => Math.max(index - 1, 0))
      return
    }
    if (event.key === 'Enter' && activeOption) {
      event.preventDefault()
      chooseOption(activeOption)
      return
    }
    if (event.key === 'Escape' && isOpen) {
      event.preventDefault()
      closeList()
    }
  }

  function handleBlur() {
    if (query !== null && query.trim() === '') {
      onChange('')
    }
    closeList()
    onBlur?.()
  }

  return (
    <div
      className={[
        'searchable-select',
        canClear ? 'searchable-select--clearable' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        role="combobox"
        className={[
          'form-control form-control-sm searchable-select__input',
          isSearchable ? '' : 'searchable-select__input--read-only',
          isInvalid ? 'is-invalid' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        value={query ?? selectedLabel}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-autocomplete={isSearchable ? 'list' : 'none'}
        aria-activedescendant={
          activeOption ? buildOptionId(activeIndex) : undefined
        }
        aria-required={isRequired || undefined}
        aria-invalid={isInvalid || undefined}
        autoComplete="off"
        readOnly={!isSearchable}
        disabled={isDisabled}
        onChange={(event) => handleInputChange(event.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={(event) => {
          if (isSearchable) {
            event.target.select()
          }
        }}
        onClick={() => {
          if (!isOpen) {
            openList()
            return
          }
          if (!isSearchable) {
            closeList()
          }
        }}
        onBlur={handleBlur}
      />
      {canClear && (
        <button
          type="button"
          className="searchable-select__clear"
          aria-label={`Clear ${ariaLabel ?? 'selection'}`}
          onMouseDown={(event) => event.preventDefault()}
          onClick={clearValue}
        >
          <X size={14} strokeWidth={2} aria-hidden="true" />
        </button>
      )}
      <ChevronDown
        className="searchable-select__chevron"
        size={16}
        aria-hidden="true"
      />
      {isOpen && (
        <div className="searchable-select__popup">
          {visibleOptions.length === 0 ? (
            <p className="searchable-select__empty">
              No options match "{query}".
            </p>
          ) : (
            <ul
              ref={listboxRef}
              id={listboxId}
              role="listbox"
              aria-label={ariaLabel}
              className="searchable-select__list"
            >
              {visibleOptions.map((option, index) => (
                <li
                  key={option.value}
                  id={buildOptionId(index)}
                  data-index={index}
                  role="option"
                  aria-selected={option.value === value}
                  className={`searchable-select__option${index === activeIndex ? ' searchable-select__option--active' : ''}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => chooseOption(option)}
                >
                  {option.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
